import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SocialMediaIcon, IconWithStatus } from 'components/icons/Catalogue';
import Button from 'components/buttons/Button';
import { urlFacebook } from 'constants/setup.constants';
import { TwitterToken } from 'services/rest/Network/twitterToken';
import { getAuthTokenSn } from 'services/rest/Network/getAuthToken';
import { InstagramToken } from 'services/rest/Network/instagramToken';
import { FacebookToken } from 'services/rest/Network/facebookToken';
import { useDispatch, useSelector } from 'react-redux';
import { selectCompany } from 'redux/slices/Company/CompanySlice';
import { useLocation } from 'react-router-dom';
import { setRouterCurrent } from 'redux/slices/Router/RouterSlice';
import {
  postNetworkToken,
  unlinkedNetwork
} from 'services/rest/Network/Network';
import {
  selectUser,
  setUserInfo,
  setUserInfoLoading
} from 'redux/slices/userSlice';
import { getInfoUser } from 'services/rest/logout';
import { Colors } from 'styles/Constants';
import InstagramLogo from 'images/logos/ig-logo.png';
import FacebookLogo from 'images/logos/fb_icon.png';
import {
  selectInvalidToken,
  removeInvalidNetwork
} from 'redux/slices/InvalidToken/InvalidTokenSlice';
import SocialComponent from 'components/SocialNetworkList';
import { ErrorAlert, SuccessAlert } from 'components/Alert';

export interface NetworkProps {
  linked: boolean;
  invalidToken: boolean;
  username: boolean;
  network: string;
}

export interface NetworksListProps {
  networks: Array<NetworkProps>;
}

const NetworksList = (props: NetworksListProps) => {
  const { networks } = props;
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { t } = useTranslation(['networks_list', 'social_network']);
  const dispatch = useDispatch();

  const { company } = useSelector(selectCompany);
  const { currentUser } = useSelector(selectUser);
  // Las que el backend acaba de confirmar como vencidas todavía pueden no venir
  // marcadas en los datos del embajador que se cargaron antes.
  const { invalidNetworks } = useSelector(selectInvalidToken);

  const { pathname } = useLocation();

  dispatch(setRouterCurrent(pathname));

  const postData = async (
    network: string,
    token: string,
    secret: string,
    verifier: string,
    screenName: string
  ) => {
    try {
      dispatch(setUserInfoLoading(true));

      const response = await postNetworkToken(
        currentUser?.token,
        {
          token: token,
          followers: 1,
          photo: '',
          uid: 1,
          secret: secret,
          verifier: verifier,
          screenName: screenName
        },
        network
      );
      if ('errors' in response) {
        setErrorMessage(t('networks_list:error_connect'));
      } else if (response.data) {
        dispatch(removeInvalidNetwork(network));
        const userInfoResponse = await getInfoUser(
          currentUser?.user?.id,
          currentUser?.token
        );
        dispatch(setUserInfo(userInfoResponse.data));
        setSuccessMessage(t('networks_list:succes_connect'));
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(t('networks_list:error_connect'));
    } finally {
      dispatch(setUserInfoLoading(false));
    }
  };

  const GetActionsForNetwork = (invalidToken: boolean) => {
    const { t } = useTranslation(['networks_list']);
    const actions = [
      {
        text: t('networks_list:profile'),
        onClick: profileSocialNetwork
      },
      {
        text: t('networks_list:unlink'),
        onClick: disconnect
      }
    ];

    if (invalidToken) {
      actions.unshift({
        text: t('networks_list:update'),
        onClick: conect
      });
    }

    return actions;
  };

  const conect = (network: string): void => {
    switch (network) {
      case 'facebook':
        Swal.fire({
          html: `
            <div class="swal-social-network">
              <img src="${FacebookLogo}" alt="Facebook Logo" class="swal-logo">
              
              ${
                company.company_permission.allowFanpageFacebook
                  ? `
                      <h4><strong>${t(
                        'social_network:fb_aco_business'
                      )}</strong></h4>
                      <p style="font-size: 16px;">${t(
                        'social_network:fb_business'
                      )}</p>
                      <button id="businessAccount" class="swal-button">
                        <span id="businessAccountText">${t(
                          'social_network:fb_but_business'
                        )}</span>
                        <span id="businessAccountSpinner" style="display: none;">
                          <div class="swal-spinner"></div>
                        </span>
                      </button>
                    `
                  : ''
              }
              
              <h4><strong>${t(
                'social_network:fb_aco_personal'
              )}</strong></h4>                        
              <p style="font-size: 16px;">${t('social_network:fb_personal')}</p>
              <button id="personalAccount" class="swal-button">
                <span id="personalAccountText">${t(
                  'social_network:fb_but_personal'
                )}</span>
                <span id="personalAccountSpinner" style="display: none;">
                  <div class="swal-spinner"></div>
                </span>
              </button>
            </div>
            <style>
              .swal-button {
                background-color: ${Colors.secondaryDarkPink};
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          `,
          showCloseButton: true,
          showConfirmButton: false,
          didOpen: () => {
            const personalAccountButton = document.getElementById(
              'personalAccount'
            );
            if (personalAccountButton) {
              personalAccountButton.addEventListener('click', () => {
                const spinner = document.getElementById(
                  'personalAccountSpinner'
                );
                const text = document.getElementById('personalAccountText');

                (personalAccountButton as HTMLButtonElement).disabled = true;
                if (text) text.textContent = t('social_network:loading');
                if (spinner) spinner.style.display = 'inline-block';

                FacebookToken(currentUser?.token, false)
                  .then(response => {
                    localStorage.removeItem('facebookConnectionType');
                    window.location.href = response.data.data;
                  })
                  .catch(error => {
                    console.error('Error personal account:', error);
                    (personalAccountButton as HTMLButtonElement).disabled = false;
                    if (text)
                      text.textContent = t('social_network:fb_but_personal');
                    if (spinner) spinner.style.display = 'none';
                    setErrorMessage(t('networks_list:error_connect'));
                  });
              });
            }
            if (company.company_permission.allowFanpageFacebook) {
              const businessAccountButton = document.getElementById(
                'businessAccount'
              );
              if (businessAccountButton) {
                businessAccountButton.addEventListener('click', () => {
                  const spinner = document.getElementById(
                    'businessAccountSpinner'
                  );
                  const text = document.getElementById('businessAccountText');

                  (businessAccountButton as HTMLButtonElement).disabled = true;
                  if (text) text.textContent = t('social_network:loading');
                  if (spinner) spinner.style.display = 'inline-block';

                  FacebookToken(currentUser?.token, true)
                    .then(response => {
                      localStorage.setItem('facebookConnectionType', 'fanpage');
                      window.location.href = response.data.data;
                    })
                    .catch(error => {
                      console.error('Error business account:', error);
                      (businessAccountButton as HTMLButtonElement).disabled = false;
                      if (text)
                        text.textContent = t('social_network:fb_but_business');
                      if (spinner) spinner.style.display = 'none';
                      setErrorMessage(t('networks_list:error_connect'));
                    });
                });
              }
            }
          }
        });
        break;

      case 'twitter':
        TwitterToken(currentUser?.token)
          .then(response => (window.location.href = response.data.data))
          .catch(error => console.log(error));
        break;

      case 'instagram':
        Swal.fire({
          html: `
            <div class="swal-social-network">
              <img src="${InstagramLogo}" alt="Instagram Logo" class="swal-logo">
              ${
                company.company_permission.allowInstagramBusiness ||
                company.id == 143
                  ? `
              <h4><strong>${t('social_network:ins_aco_business')}</strong></h4>
              <p style="font-size: 16px;">${t(
                'social_network:ins_business'
              )}</p>
              <button id="businessAccount" class="swal-button">
                <span id="businessAccountText">${t(
                  'social_network:ins_but_business'
                )}</span>
                <span id="businessAccountSpinner" style="display: none;">
                  <div class="swal-spinner"></div>
                </span>
              </button>
                  `
                  : ''
              }
              <h4><strong>${t(
                'social_network:ins_aco_personal'
              )}</strong></h4>                        
              <p style="font-size: 16px;">${t(
                'social_network:ins_personal'
              )}</p>
              <button id="personalAccount" class="swal-button">
                <span id="personalAccountText">${t(
                  'social_network:ins_but_personal'
                )}</span>
                <span id="personalAccountSpinner" style="display: none;">
                  <div class="swal-spinner"></div>
                </span>
              </button>
            </div>
            <style>
              .swal-button {
                background-color: ${Colors.secondaryDarkPink};
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          `,
          showCloseButton: true,
          showConfirmButton: false,
          didOpen: () => {
            const personalAccountButton = document.getElementById(
              'personalAccount'
            );
            if (personalAccountButton) {
              personalAccountButton.addEventListener('click', () => {
                const spinner = document.getElementById(
                  'personalAccountSpinner'
                );
                const text = document.getElementById('personalAccountText');

                (personalAccountButton as HTMLButtonElement).disabled = true;
                if (text) {
                  text.textContent = t('social_network:loading');
                }
                if (spinner) {
                  spinner.style.display = 'inline-block';
                }

                Swal.close();

                Swal.fire({
                  title: 'Vincular cuenta personal',
                  html: `
                    <div class="swal-social-network">
                      <input id="swal-input1" class="swal2-input" placeholder="${t(
                        'social_network:UserName'
                      )}" style="margin: 0;">
                      <div id="swal-spinner-container" style="display: none; margin-top: 20px;">
                        <button class="swal-button">
                          <span>${t('social_network:connecting')}</span>
                          <div class="swal-spinner"></div>
                        </button>
                      </div>
                    </div>
                    <style>
                      .swal-button {
                        background-color: ${Colors.secondaryDarkPink};
                      }
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    </style>
                  `,
                  showCancelButton: true,
                  cancelButtonText: 'Regresar',
                  confirmButtonText: t('networks_list:connect'),
                  confirmButtonColor: Colors.secondaryPurple,
                  cancelButtonColor: Colors.secondaryDarkBlue,
                  focusConfirm: false,
                  preConfirm: () => {
                    return new Promise((resolve, reject) => {
                      const usernameElement = document.getElementById(
                        'swal-input1'
                      );
                      const username = usernameElement
                        ? (usernameElement as HTMLInputElement).value
                        : '';
                      const spinnerContainer = document.getElementById(
                        'swal-spinner-container'
                      );
                      if (spinnerContainer) {
                        spinnerContainer.style.display = 'block';
                      }
                      const confirmButton = document.querySelector(
                        '.swal2-confirm'
                      );
                      const cancelButton = document.querySelector(
                        '.swal2-cancel'
                      );
                      if (
                        confirmButton &&
                        confirmButton instanceof HTMLElement
                      ) {
                        confirmButton.style.display = 'none';
                      }
                      if (cancelButton && cancelButton instanceof HTMLElement) {
                        cancelButton.style.display = 'none';
                      }

                      postData('instagram', 'code', '', '', username)
                        .then(() => {
                          resolve(true);
                        })
                        .catch(error => {
                          reject(error);
                        });
                    });
                  },
                  allowOutsideClick: () => !Swal.isLoading()
                })
                  .then(result => {
                    (personalAccountButton as HTMLButtonElement).disabled = false;
                    if (text) {
                      text.textContent = t('social_network:ins_but_personal');
                    }
                    if (spinner) {
                      spinner.style.display = 'none';
                    }
                    if (result.dismiss === Swal.DismissReason.cancel) {
                      conect('instagram');
                    }
                  })
                  .catch(error => {
                    (personalAccountButton as HTMLButtonElement).disabled = false;
                    if (text) {
                      text.textContent = t('social_network:ins_but_personal');
                    }
                    if (spinner) {
                      spinner.style.display = 'none';
                    }

                    setErrorMessage(t('networks_list:error_connect'));
                  });
              });
            }

            const businessAccountButton = document.getElementById(
              'businessAccount'
            );
            if (businessAccountButton) {
              businessAccountButton.addEventListener('click', () => {
                const spinner = document.getElementById(
                  'businessAccountSpinner'
                );
                const text = document.getElementById('businessAccountText');

                (businessAccountButton as HTMLButtonElement).disabled = true;
                if (text) {
                  text.textContent = t('social_network:loading');
                }
                if (spinner) {
                  spinner.style.display = 'inline-block';
                }

                InstagramToken(currentUser?.token)
                  .then(response => {
                    window.location.href = response.data.data;
                  })
                  .catch(error => {
                    (businessAccountButton as HTMLButtonElement).disabled = false;
                    if (text) {
                      text.textContent = t('social_network:ins_but_business');
                    }
                    if (spinner) {
                      spinner.style.display = 'none';
                    }
                  });
              });
            }
          }
        });
        break;

      case 'linkedin':
        getAuthTokenSn(currentUser?.token, 'linkedin')
          .then(response => (window.location.href = response.data.data))
          .catch(error => console.log(error));
        break;

      case 'tiktok':
        getAuthTokenSn(currentUser?.token, 'tiktok')
          .then(response => (window.location.href = response.data.data))
          .catch(error => console.log(error));
        break;

      default:
        break;
    }
  };

  const profileSocialNetwork = (network: string) => {
    let linkProfile;
    switch (network) {
      case 'facebook':
        linkProfile = `${networks[0].username}`;
        break;
      case 'twitter':
        linkProfile = `https://twitter.com/${networks[1].username}`;
        break;
      case 'linkedin':
        linkProfile = `https://www.linkedin.com/in/${networks[2].username}`;
        break;
      case 'instagram':
        linkProfile = `https://www.instagram.com/${networks[3].username}`;
        break;
      case 'tiktok':
        linkProfile = `https://www.tiktok.com/@${networks[4].username}`;
        break;
    }
    window.open(linkProfile, '_blank');
  };

  const disconnect = (network: string) => {
    unlinkedNetwork(currentUser?.token, network)
      .then(response => {
        if (response.data) {
          dispatch(setUserInfoLoading(true));
          getInfoUser(currentUser?.user?.id, currentUser?.token).then(
            response => {
              dispatch(setUserInfo(response.data));
              dispatch(setUserInfoLoading(false));
              setSuccessMessage(t('networks_list:succes_disconnect'));
            }
          );
        } else {
          setErrorMessage(t('networks_list:error_disconnect'));
        }
      })
      .catch(error => {
        dispatch(setUserInfoLoading(false));
      });
  };

  return (
    <>
      {networks.map((networkItem: any, index: any) => {
        const { linked, network } = networkItem;
        const invalidToken =
          networkItem.invalidToken || invalidNetworks.includes(network);
        if (linked) {
          const status = invalidToken ? 'alert' : 'success';

          return (
            <>
              {successMessage && (
                <SuccessAlert
                  text={successMessage}
                  allowClose={true}
                  onClose={() => setSuccessMessage(null)}
                />
              )}
              {errorMessage && (
                <ErrorAlert
                  text={errorMessage}
                  allowClose={true}
                  onClose={() => setErrorMessage(null)}
                />
              )}
              <SocialComponent
                invalidToken={invalidToken}
                network={network}
                status={status}
                items={GetActionsForNetwork}
              />
            </>
          );
        } else {
          return (
            <React.Fragment key={index}>
              <Button
                className="hide-on-mobile"
                variant={network}
                size="small"
                icon={network}
                value={t('networks_list:connect')}
                onClick={() => conect(network)}
              />
              <IconWithStatus
                onClick={() => conect(network)}
                className="show-on-mobile connect-account"
                status="danger"
              >
                <SocialMediaIcon
                  size="medium"
                  variant="disabled"
                  shape="square"
                  name={network}
                />
              </IconWithStatus>
            </React.Fragment>
          );
        }
      })}
    </>
  );
};

export default NetworksList;
